// Corridor Zero — a real sprite/billboard raycaster FPS. Alan Turing (the hero,
// seen in the HUD face and first-person hands, Bremont Codebreaker on the left
// wrist) clears a Zero Trust corridor of Cloudflare-competitor parody sentinels.
//
// Contract notes:
// - The constructor and the advanceTime/update path are DOM-free so the class
//   instantiates and simulates under the headless test runner. All canvas /
//   sprite creation is deferred to the first render() via ensureGfx().
// - advanceTime() accumulates the round clock; the first update() past the round
//   length flips `ended` with a fixed message (deterministic round-end test).

import type { SoundEffect } from "../../core/audio";
import { SeededRandom } from "../../core/random";
import type { GameHud, GameInstance, GameServices } from "../../core/types";

import { Enemy, pickTarget, type EnemyEnv } from "./enemies";
import { buildLevel } from "./level";
import { Player } from "./player";
import { Raycaster, type RenderSprite } from "./raycaster";
import {
  CORRIDOR_ZERO_ARCHETYPES,
  buildRoster,
  type ActorSprites,
  type ArchetypeSpec,
} from "./sprites";
import {
  loadCorridorZeroPickups,
  loadCorridorZeroRoster,
  type PickupSprites,
} from "./sprite-loader";
import { createProceduralTextures } from "./textures";
import type { Level, SpriteFrame, TextureProvider } from "./types";
import { Weapon } from "./weapons";
import { drawHud, type HudState } from "./hud";

const ROUND_SECONDS = 60;
const END_MESSAGE = "Corridor secured";
const DOWN_MESSAGE = "You were overrun";
const INTRO = "Verify trusted gates. Intercept the intruders.";

const SOUND_MAP: Record<string, SoundEffect> = {
  shoot: "miss",
  hit: "hit",
  enemyShoot: "miss",
  enemyPain: "hit",
  enemyDie: "hit",
  playerHit: "miss",
};

interface Gfx {
  readonly ray: Raycaster;
  /** Active enemy roster. Starts procedural, swapped to AI art when it loads. */
  roster: Map<string, ActorSprites>;
  /** AI world billboards (pickups + gate); null until loaded (or on failure). */
  pickups: PickupSprites | null;
}

/** Fixed decorative billboard positions (visual only; no sim/pickup logic). */
interface WorldProp {
  readonly x: number;
  readonly y: number;
  readonly sprite: "trustToken" | "integrityOrb" | "checkpointGate";
  readonly scale: number;
}

const WORLD_PROPS: readonly WorldProp[] = [
  { x: 12.5, y: 4.5, sprite: "checkpointGate", scale: 0.95 }, // gate framing arena C entrance
  { x: 12.5, y: 12.5, sprite: "checkpointGate", scale: 0.95 }, // gate on the C->E spine
  { x: 5.5, y: 16.5, sprite: "trustToken", scale: 0.32 }, // token in the start room
  { x: 20.5, y: 4.5, sprite: "trustToken", scale: 0.32 }, // token in arena C
  { x: 22.5, y: 17.5, sprite: "integrityOrb", scale: 0.32 }, // orb in room D
  { x: 12.5, y: 17.5, sprite: "integrityOrb", scale: 0.32 }, // orb in connector E
];

export class CorridorZeroGame implements GameInstance {
  private readonly random = new SeededRandom((Date.now() ^ 0x403) >>> 0);
  private readonly level: Level;
  private readonly player: Player;
  private readonly enemies: Enemy[];
  private readonly weapon = new Weapon();
  private readonly textures: TextureProvider = createProceduralTextures();

  private score = 0;
  private elapsed = 0;
  private time = ROUND_SECONDS;
  private message = INTRO;
  private messageTimer = 0;
  private hurt = 0;
  private moving = false;
  private ended = false;
  private gfx: Gfx | null = null;

  constructor(private readonly services: GameServices) {
    this.level = buildLevel();
    this.player = new Player(this.level.player);
    const specByKind = new Map<string, ArchetypeSpec>(
      CORRIDOR_ZERO_ARCHETYPES.map((s) => [s.kind, s]),
    );
    this.enemies = this.level.enemies.map((sp) => {
      const spec = specByKind.get(sp.kind) ?? CORRIDOR_ZERO_ARCHETYPES[0]!;
      return new Enemy({ x: sp.x, y: sp.y }, spec.kind, spec.health, spec.score);
    });
  }

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    if (this.messageTimer === 0 && this.message !== INTRO) this.message = INTRO;
    this.hurt = Math.max(0, this.hurt - deltaSeconds * 2);

    // Deterministic round end (checked before simulation so the message is fixed).
    if (this.time === 0) {
      this.finish(END_MESSAGE);
      return;
    }

    const moved = this.player.update(this.services.input, deltaSeconds, this.level);
    this.moving = moved > 0.001;
    this.weapon.update(deltaSeconds, this.moving);

    const env = this.makeEnv();
    for (const enemy of this.enemies) enemy.update(deltaSeconds, env);

    if (this.player.health <= 0) {
      this.finish(DOWN_MESSAGE);
      return;
    }

    if (this.services.input.isDown("primary") && this.weapon.tryFire()) {
      this.fire(env);
    }

    if (this.enemies.every((enemy) => !enemy.alive)) {
      this.finish(END_MESSAGE);
      return;
    }
  }

  render(context: CanvasRenderingContext2D): void {
    const { ray, roster } = this.ensureGfx();
    const cam = { x: this.player.x, y: this.player.y, angle: this.player.angle };

    ray.renderWorld(this.level, this.textures, cam, 0);

    const sprites: RenderSprite[] = [];
    const pickups = this.gfx?.pickups ?? null;
    if (pickups) {
      for (const prop of WORLD_PROPS) {
        const frame: SpriteFrame = pickups[prop.sprite];
        sprites.push({ x: prop.x, y: prop.y, frame, worldScale: prop.scale });
      }
    }
    for (const enemy of this.enemies) {
      const set = roster.get(enemy.kind);
      if (!set) continue;
      sprites.push(enemy.getRenderSprite(set));
    }
    ray.drawSprites(sprites, cam, 0);
    ray.present(context, 0, 0, 1280, 720);

    this.weapon.draw(context, { moving: this.moving, reducedMotion: this.services.reducedMotion });
    drawHud(context, this.hudState());
  }

  getHud(): GameHud {
    return { score: this.score, time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {
    this.gfx = null;
  }

  // --- internals ---------------------------------------------------------

  private ensureGfx(): Gfx {
    if (this.gfx) return this.gfx;
    // Start with the procedural roster so the first frame always draws; swap in
    // the AI-baked art the moment it decodes. On any load failure we simply keep
    // the procedural fallback (and, for props, draw nothing).
    const gfx: Gfx = {
      ray: new Raycaster(384, 216),
      roster: buildRoster(CORRIDOR_ZERO_ARCHETYPES),
      pickups: null,
    };
    this.gfx = gfx;
    loadCorridorZeroRoster()
      .then((roster) => {
        if (this.gfx === gfx) gfx.roster = roster;
      })
      .catch(() => {
        /* keep procedural roster */
      });
    loadCorridorZeroPickups()
      .then((pickups) => {
        if (this.gfx === gfx) gfx.pickups = pickups;
      })
      .catch(() => {
        /* no world props */
      });
    return gfx;
  }

  private makeEnv(): EnemyEnv {
    return {
      level: this.level,
      player: { x: this.player.x, y: this.player.y },
      hurtPlayer: (amount: number): void => {
        this.player.damage(amount);
        this.hurt = 1;
        this.playSound("playerHit");
        this.setMessage("Under fire", 0.8);
      },
      onDeath: (scoreValue: number): void => {
        this.score += scoreValue;
        this.setMessage("Intruder down", 1);
      },
      playSound: (name: string): void => this.playSound(name),
      rng: (): number => this.random.next(),
    };
  }

  private fire(env: EnemyEnv): void {
    this.playSound("shoot");
    const spread = (this.random.next() - 0.5) * 0.05;
    const angle = this.player.angle + spread;
    const dirX = Math.cos(angle);
    const dirY = Math.sin(angle);
    const target = pickTarget(this.enemies, this.player.x, this.player.y, dirX, dirY, this.level, 22);
    if (target) target.hit(24 + this.random.next() * 12, env);
  }

  private playSound(name: string): void {
    this.services.audio.play(SOUND_MAP[name] ?? "confirm");
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${this.score}.`);
  }

  private hudState(): HudState {
    return {
      health: this.player.health,
      armor: this.player.armor,
      ammoInMag: this.weapon.ammoInMag,
      magazine: this.weapon.magazine,
      reloading: this.weapon.reloading,
      score: this.score,
      enemiesLeft: this.enemies.reduce((n, e) => n + (e.alive ? 1 : 0), 0),
      hurt: this.hurt,
      elapsed: this.elapsed,
      reducedMotion: this.services.reducedMotion,
    };
  }
}
